import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import { QuestionType, type QuestionItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";

import { QuestionCardItemsWrapper } from "../../../../system/question/card/card-items-wrapper";
import { QuestionPreQuestionItemOptions } from "../pre-question-item-options";

type Props = {
	question: ForAutoGenerator<QuestionItemInput, "">;
	mainSectionClassName?: string;

	onSetIsRequired: (isRequired: boolean) => void;
	onSetTitle: (input: string) => void;
	onSetQuestionType: (questionType: QuestionType) => void;

	appendOptionItem: () => void;
	removeOptionItem: (optionIndex: number) => void;

	onSetOptionItem: (optionIndex: number) => (input: string) => void;
};

export const QuestionPreQuestionItem = ({
	question,
	mainSectionClassName,
	onSetIsRequired,
	onSetTitle,
	onSetQuestionType: _onSetQuestionType,

	appendOptionItem,

	removeOptionItem,

	onSetOptionItem,
}: Props) => {
	const onSetQuestionType = useCallback(
		(input: string) => {
			const value = input as QuestionType;
			_onSetQuestionType(value);
		},
		[_onSetQuestionType],
	);

	const uniqueID = useId();
	const questionTypeOptions = useMemo(
		() =>
			[
				{ id: `0_${uniqueID}`, label: "ユーザによるテキスト入力", value: QuestionType.Free },
				{
					id: `1_${uniqueID}`,
					label: "選択肢から１つを選択",
					value: QuestionType.Radio,
				},
				{
					id: `2_${uniqueID}`,
					label: "選択肢から複数選択",
					value: QuestionType.Select,
				},
			] satisfies FormOptionType[],
		[uniqueID],
	);

	const accordionValue = question.questionID?.value ?? uniqueID;
	const hasDetailError = [
		question.required?.error,
		question.questionType.error,
		question.options?.hasError,
	].some(Boolean);

	// defaultValue は初回マウント時のみ効くため、保存バリデーション後に付いたエラーでは再展開されない。
	// 制御モード + hasDetailError への同期で、詳細内エラー時は必ず開く。
	const [detailAccordionValue, setDetailAccordionValue] = useState<string>(() =>
		hasDetailError ? accordionValue : "",
	);

	useEffect(() => {
		if (hasDetailError) {
			setDetailAccordionValue(accordionValue);
		}
	}, [accordionValue, hasDetailError]);

	return (
		<div className={cn("relative flex flex-col gap-y-6", mainSectionClassName)}>
			<TextAreaField
				label="質問"
				value={question.question.value}
				handleChange={onSetTitle}
				errorMessage={question.question.error}
			/>

			<Accordion
				type="single"
				collapsible
				value={detailAccordionValue}
				onValueChange={setDetailAccordionValue}
			>
				<AccordionItem value={accordionValue}>
					<AccordionTrigger>質問の詳細設定</AccordionTrigger>

					<AccordionContent>
						<div className="flex flex-col gap-y-6">
							<CheckboxField
								value={question.required?.value ?? false}
								label={"回答必須にする"}
								handleChange={onSetIsRequired}
								errorMessage={question.required?.error}
							/>

							<RadioGroupField
								label="質問タイプ"
								value={question.questionType.value}
								options={questionTypeOptions}
								handleValueChange={onSetQuestionType}
								errorMessage={question.questionType.error}
							/>

							{(question.questionType.value === QuestionType.Radio ||
								question.questionType.value === QuestionType.Select) && (
								<QuestionCardItemsWrapper appendItem={appendOptionItem}>
									{Boolean(question.options?.value?.length) &&
										question.options?.value!.map((item, idx) => {
											return (
												<QuestionPreQuestionItemOptions
													key={`${idx}_${uniqueID}`}
													question={question.question.value}
													onSetOptionItem={onSetOptionItem(idx)}
													option={item}
													removeOptionItem={() => removeOptionItem(idx)}
												/>
											);
										})}
								</QuestionCardItemsWrapper>
							)}
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

QuestionPreQuestionItem.displayName = "QuestionPreQuestionItem";
